// NOMBASE — API Service (cookie-only auth).
//
// Auth was removed from the frontend May 2026. Every request below
// passes `credentials: 'include'` so the `nombase_session` HttpOnly
// cookie minted by `/api/auth/dev-bootstrap` flows automatically.
// No Authorization header is constructed here.
//
// 401s are propagated to the caller without side effects — AuthContext
// handles re-running the dev_bootstrap shim on 401.

import { newIdempotencyKey } from '@/lib/idempotency';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Hard request timeout. fetch() has no built-in timeout — a cold-start
// backend, K8s pod swap, or Cloudflare cold-edge can leave a fetch
// hanging for many minutes. We surface that as a normal rejection so
// callers (notably the AuthContext spinner-clearing logic) make
// progress instead of leaving the UI stuck on a centered spinner.
// 15s is generous; anything that takes longer in normal operation
// belongs on a background queue, not the auth boot path.
const REQUEST_TIMEOUT_MS = 15_000;

// ── In-flight GET deduplication ─────────────────────────────────
// Multiple components mounting simultaneously (BrandDashboard,
// AppShell, OnboardingGate, AuthContext) frequently fire the same
// GET within the same tick. Coalescing them into one in-flight
// Promise drops dashboard cold-load from ~25 calls to ~10. The
// cache is keyed on `method + url` (the cookie is implicit and the
// same for the whole tab) and entries are evicted as soon as the
// underlying fetch settles.
const _inflight = new Map();

function _inflightKey(url, method) {
  return method + ' ' + url;
}

// ── Backend back-pressure circuit breaker ───────────────────────
// When the backend returns 5xx (502/503/504/520 — nginx/Cloudflare
// telling us "I can't reach uvicorn right now"), pause new
// non-critical GETs for a short window so the backend can recover
// instead of being further hammered by retries. Critical paths
// (auth, signup-onboarding/status) are never tripped — they're
// what gets the user back to a working state. Timeouts are short
// (1.5s base, 15s cap) so a transient pod swap doesn't lock the UI.
let _breakerOpenUntil = 0;
function _breakerOpen() {
  return Date.now() < _breakerOpenUntil;
}
function _trip(reason) {
  const since = _breakerOpenUntil ? _breakerOpenUntil - Date.now() : 0;
  const next  = Math.min(Math.max(1500, since * 2), 15_000);
  _breakerOpenUntil = Date.now() + next;
  // eslint-disable-next-line no-console
  console.warn(`[api] backend back-pressure (${reason}) — pausing non-critical GETs for ${next}ms`);
}
function _resetBreaker() { _breakerOpenUntil = 0; }
function _isCriticalPath(endpoint) {
  // Allow these through even when breaker is open. They're either
  // auth-bridge paths or single-call boot probes.
  return /^(\/api)?\/(auth\/(whoami|me|tenant|logout)|signup-onboarding\/status|health|config\/public)/.test(endpoint);
}

// Helper for API requests
async function apiRequest(endpoint, options = {}) {
  // ── May 2026 CDN cache-storm bypass ──────────────────────────
  // During the 11h prod outage, Cloudflare globally cached 5xx
  // responses for `/api/*` on multiple edges. After origin recovered,
  // users on those edges kept seeing stale 502s across multiple ISPs
  // / WiFi networks because each network mapped to a different
  // already-poisoned edge. The backend now emits strict
  // `CDN-Cache-Control: no-store` (server.py:add_cache_headers) which
  // prevents FUTURE caching — but existing cache entries don't expire
  // until their TTL.
  //
  // Belt-and-braces: append a per-request cache-busting query param
  // so every request URL is unique. Cloudflare keys its cache by URL,
  // so a unique URL == guaranteed cache miss == fresh origin fetch.
  // The backend ignores unknown query params, so this is a pure
  // client-side defensive measure. Cost: ~12 bytes per URL.
  //
  // Critical: ALWAYS apply, even on GET, even on already-tagged URLs.
  // The whole point is to break out of the poisoned-cache trap.
  const method = (options.method || 'GET').toUpperCase();
  const _cb = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  const _sep = endpoint.includes('?') ? '&' : '?';
  const url = `${API_BASE}/api${endpoint}${_sep}_cb=${_cb}`;

  const headers = {
    'Content-Type': 'application/json',
    // Request-level Cache-Control. Belt-and-braces with the query-
    // param buster above: if the URL change somehow doesn't break the
    // cache lookup (e.g. a CDN normalizes query strings), this still
    // forces revalidation to origin.
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma':        'no-cache',
    ...options.headers,
  };

  // Auth: cookie-only. The `nombase_session` HttpOnly cookie minted
  // by `/api/auth/dev-bootstrap` rides along on every request via
  // `credentials: 'include'` (set below). No Authorization header is
  // constructed here.

  // PR-12 idempotency: opt-in. Callers pass `idempotencyKey` in
  // `options` to send an `Idempotency-Key` header. The backend dedupes
  // retries against (tenant, route, key) for 24h.
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  // ── Circuit-breaker gate (GET only, non-critical paths) ────────
  if (method === 'GET' && _breakerOpen() && !_isCriticalPath(endpoint)) {
    throw new Error('Backend temporarily overloaded — retrying in a moment');
  }

  // ── In-flight GET dedupe ───────────────────────────────────────
  // Key off `endpoint` (not the cache-busted `url`) so concurrent
  // GETs to the same logical endpoint still dedupe to a single
  // in-flight request. Without this fix, the per-request cache-buster
  // above would defeat dedupe entirely and concurrent mounts of the
  // same component would fan out to N origin calls.
  const dedupeKey =
    method === 'GET' && !options.signal && !options.idempotencyKey
      ? _inflightKey(endpoint, method)
      : null;
  if (dedupeKey && _inflight.has(dedupeKey)) {
    return _inflight.get(dedupeKey);
  }

  // AbortController-based hard timeout. We don't override an upstream
  // signal if the caller passed one (e.g. a React cleanup).
  const controller = options.signal ? null : new AbortController();
  const signal = options.signal || controller?.signal;
  const timer  = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null;

  const exec = (async () => {
    let response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
        signal,
        // ── Auth ─────────────────────────────────────────────
        // Clerk JWT is attached via the `Authorization: Bearer …`
        // header by the token bridge in `context/AuthContext.js`
        // (see `registerGetToken`). `credentials: 'include'` ships
        // the legacy `nombase_session` cookie alongside it for the
        // dev_bootstrap fallback path; harmless in production
        // where Clerk is authoritative.
        credentials: 'include',
      });
    } catch (e) {
      if (e?.name === 'AbortError') {
        throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
      }
      _trip('network');
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (response.status >= 500) {
      _trip(`HTTP ${response.status}`);
    } else if (response.ok) {
      _resetBreaker();
    }

    // Handle 401 unauthorized — propagate the error and let
    // AuthContext / route guards decide what to do (redirect to the
    // front door, show empty state, etc.). We DO NOT redirect from
    // here: it would race with AuthContext bootstrap and cause
    // tight redirect loops when a single API endpoint transiently
    // 401s a brand-new user.
    if (response.status === 401) {
      const body = await response.json().catch(() => ({ detail: 'Unauthorized' }));
      throw new Error(body.detail || 'Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  })();

  if (dedupeKey) {
    _inflight.set(dedupeKey, exec);
    exec.finally(() => _inflight.delete(dedupeKey));
  }
  return exec;
}

// Re-exported so `useApiQuery` (and any future thin hook) can route
// through the same in-flight dedupe + circuit-breaker logic without
// duplicating it. Endpoints passed in MUST NOT include the `/api`
// prefix — apiRequest adds it.
export { apiRequest };

// Generic API client for networkAPI.js and other services
// Provides get, post, patch, delete, upload methods
export const apiClient = {
  get: (endpoint) => {
    // Remove /api prefix if present since apiRequest adds it
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    return apiRequest(cleanEndpoint);
  },

  post: (endpoint, data) => {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    return apiRequest(cleanEndpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  patch: (endpoint, data) => {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    return apiRequest(cleanEndpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete: (endpoint) => {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    return apiRequest(cleanEndpoint, {
      method: 'DELETE',
    });
  },

  upload: async (endpoint, file, additionalData = {}) => {
    const cleanEndpoint = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    const url = `${API_BASE}/api${cleanEndpoint}`;

    const formData = new FormData();
    formData.append('file', file);

    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (response.status === 401) {
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }
};

// ============================================
// AUTHENTICATION
// ============================================
// Sign-in / sign-up are owned by Clerk's embedded `<SignIn/>` and
// `<SignUp/>` components mounted at `/sign-in` and `/sign-up` (see
// App.js). This module only reads identity / tenant from the
// authenticated backend session.
export const authAPI = {
  async getMe() {
    return apiRequest('/auth/me');
  },

  async getTenant() {
    return apiRequest('/auth/tenant');
  },

  async getTeam() {
    return apiRequest('/auth/team');
  },

  async inviteTeamMember(data) {
    return apiRequest('/auth/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTeamMember(userId, role) {
    return apiRequest(`/auth/team/${userId}?role=${role}`, {
      method: 'PATCH',
    });
  },

  async removeTeamMember(userId) {
    return apiRequest(`/auth/team/${userId}`, {
      method: 'DELETE',
    });
  },

  async updateTenant(data) {
    return apiRequest('/auth/tenant', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Drops the session cookie server-side. Phase 2 will land the
  // `/api/auth/logout` endpoint; until then a 404 here is benign and
  // caught by AuthContext.logout().
  async logout() {
    return apiRequest('/auth/logout', { method: 'POST' });
  },

  // Sliding-expiry session refresh. The browser sends the existing
  // `nombase_session` cookie automatically (credentials:"include");
  // backend reissues a fresh cookie + returns the new TTL so the
  // client can schedule the next call. Cookie-only — no Bearer.
  // Returns: { ok, user, ttl_seconds, expires_at, reissued: true }.
  async refreshSession() {
    return apiRequest('/auth/session/refresh', { method: 'POST' });
  },

  // Mint a fresh operator session cookie. Used by AuthContext when
  // /auth/me returns 401 — instead of bouncing through a full page
  // reload (which can race with cookie commits in some browser
  // configurations) we POST to dev-bootstrap inline, then retry
  // /auth/me in the same execution context. Same cookie jar, same
  // tab lifecycle — no reload-loop edge cases.
  async devBootstrap() {
    return apiRequest('/auth/dev-bootstrap', {
      method: 'POST',
      body: '{}',
    });
  },

  // Defence-in-depth Gateway SSO exchange. The Gateway's documented
  // flow redirects the browser directly to `GET /api/sso?token=…` and
  // the backend serves a 302 → `return_to`. If, however, a `?token=`
  // somehow lands on a React route (e.g. a future Gateway flow variant
  // that targets a SPA route instead of the API), AuthContext.loadIdentity
  // calls this method to exchange the token client-side. The `Accept`
  // and `X-Sso-Format` headers signal the JSON branch of the same
  // endpoint, which sets the `nombase_session` cookie and returns
  // `{ok, user, return_to}` so we DON'T follow the redirect into the
  // root HTML and choke trying to JSON-parse it.
  async ssoExchange(token) {
    return apiRequest(`/sso?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Sso-Format': 'json',
      },
      body: '{}',
    });
  },

  // Legacy synchronous gate retained for backward-compatibility with
  // a handful of components (notifications poller, AppShell guards,
  // RequestList* etc.) that pre-dated cookie auth. The HttpOnly
  // `nombase_session` cookie is not visible to JS, so we can't
  // introspect it directly here — but all callsites of this method
  // run AFTER the route-level unauth gate in `App.js`, which means
  // they only execute when the user is in fact authenticated. A
  // permissive `true` is the correct contract; the actual auth
  // enforcement happens server-side on every API call.
  isAuthenticated() {
    return true;
  },
};

// ============================================
// PRODUCTS
// ============================================
export const productsAPI = {
  async search(query, filters = {}) {
    const params = new URLSearchParams({ q: query, ...filters });
    return apiRequest(`/products/search?${params}`);
  },

  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/products?${params}`);
  },

  async getById(productId) {
    return apiRequest(`/products/${productId}`);
  },

  async create(productData) {
    return apiRequest('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },

  async update(productId, productData) {
    return apiRequest(`/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },

  async delete(productId) {
    return apiRequest(`/products/${productId}`, {
      method: 'DELETE',
    });
  }
};

// ============================================
// TENANTS (SUPPLIERS/BRANDS)
// ============================================
export const tenantsAPI = {
  async getSuppliers(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/tenants/suppliers?${params}`);
  },

  async searchSuppliers(query, filters = {}) {
    const params = new URLSearchParams({ q: query, ...filters });
    return apiRequest(`/tenants/suppliers/search?${params}`);
  },

  async getSupplierById(supplierId) {
    return apiRequest(`/tenants/suppliers/${supplierId}`);
  },

  async getTenantById(tenantId) {
    return apiRequest(`/tenants/${tenantId}`);
  },

  async updateTenant(tenantId, data) {
    return apiRequest(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
};

// ============================================
// ORDERS
// ============================================
export const ordersAPI = {
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/orders?${params}`);
  },

  async getById(orderId) {
    return apiRequest(`/orders/${orderId}`);
  },

  async create(orderData) {
    return apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  async updateStatus(orderId, status) {
    return apiRequest(`/orders/${orderId}/status?new_status=${status}`, {
      method: 'PUT',
    });
  }
};

// ============================================
// SAVED SUPPLIERS
// ============================================
// Saved suppliers was part of the RFQ / marketplace surface, removed in the
// brand-only MRP excision (the backend route was deleted). These are inert
// stubs so any lingering caller resolves to an empty result instead of firing
// a 404 against the removed endpoint. Remove the remaining UI in a follow-up.
export const savedSuppliersAPI = {
  async getAll() { return []; },
  async checkSaved() { return { saved: false }; },
  async save() { return null; },
  async promote() { return null; },
  async update() { return null; },
  async remove() { return null; },
};

// ============================================
// MESSAGES / CHAT
// ============================================
// (messagesAPI removed — supplier messaging was excised with the marketplace/RFQ system.)

// ============================================
// REQUESTS / RFQ
// ============================================
export const requestsAPI = {
  // Brand endpoints
  async create(requestData) {
    return apiRequest('/requests', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  },

  async getBrandRequests(status = null) {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/requests/brand${params}`);
  },

  async acceptQuote(requestId) {
    return apiRequest(`/requests/${requestId}/accept`, { method: 'POST' });
  },

  async declineQuote(requestId, message) {
    return apiRequest(`/requests/${requestId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  async negotiate(requestId, counterItems, message) {
    return apiRequest(`/requests/${requestId}/negotiate`, {
      method: 'POST',
      body: JSON.stringify({ counter_items: counterItems, message }),
    });
  },

  async requestSample(requestId, message) {
    return apiRequest(`/requests/${requestId}/request-sample`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  },

  async approveSample(requestId) {
    return apiRequest(`/requests/${requestId}/approve-sample`, { method: 'POST' });
  },

  // Supplier endpoints
  async getSupplierRequests(status = null) {
    const params = status ? `?status=${status}` : '';
    return apiRequest(`/requests/supplier${params}`);
  },

  async submitQuote(requestId, quoteData) {
    return apiRequest(`/requests/${requestId}/quote`, {
      method: 'POST',
      body: JSON.stringify(quoteData),
    });
  },

  async supplierNegotiate(requestId, counterItems, message) {
    return apiRequest(`/requests/${requestId}/supplier-negotiate`, {
      method: 'POST',
      body: JSON.stringify({ counter_items: counterItems, message }),
    });
  },

  async sendSample(requestId, trackingInfo) {
    return apiRequest(`/requests/${requestId}/send-sample`, {
      method: 'POST',
      body: JSON.stringify({ message: trackingInfo }),
    });
  },

  async supplierDecline(requestId, reason) {
    return apiRequest(`/requests/${requestId}/supplier-decline`, {
      method: 'POST',
      body: JSON.stringify({ message: reason }),
    });
  },

  // Common
  async getById(requestId) {
    return apiRequest(`/requests/${requestId}`);
  },

  async getBrandStats() {
    return apiRequest('/requests/stats/brand');
  },

  async getSupplierStats() {
    return apiRequest('/requests/stats/supplier');
  },

  async acceptQuoteAndCreatePO(requestId, createPO = true) {
    return apiRequest(`/requests/${requestId}/accept-quote`, {
      method: 'POST',
      body: JSON.stringify({ create_po: createPO }),
      idempotencyKey: newIdempotencyKey(),
    });
  },

  async getSupplierMatching(data) {
    return apiRequest('/requests/supplier-matching', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async bulkFromBom(data) {
    return apiRequest('/requests/bulk-from-bom', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
};

// ============================================
// DOCUMENTS
// ============================================
export const documentsAPI = {
  async getAll(filters = {}) {
    const params = new URLSearchParams(filters);
    return apiRequest(`/documents?${params}`);
  },

  async getById(documentId) {
    return apiRequest(`/documents/${documentId}`);
  },

  async upload(file, metadata) {
    const url = `${API_BASE}/api/documents/upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name);
    formData.append('doc_type', metadata.type);
    if (metadata.batch_lot) formData.append('batch_lot', metadata.batch_lot);
    if (metadata.expiry_date) formData.append('expiry_date', metadata.expiry_date);
    if (metadata.notes) formData.append('notes', metadata.notes);
    if (metadata.related_product_id) formData.append('related_product_id', metadata.related_product_id);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  },

  async create(documentData) {
    return apiRequest('/documents', {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  },

  async delete(documentId) {
    return apiRequest(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  getFileUrl(filename) {
    return `${API_BASE}/api/documents/file/${filename}`;
  },

  // ── Documents Hub (Wave 5A) ───────────────────────────────────────
  // Faceted folder read: every CURRENT document for the tenant, with
  // counts by entity_type/doc_type/status/expiry + needs_review.
  async folder(filters = {}) {
    const clean = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const params = new URLSearchParams(clean);
    return apiRequest(`/documents/folder?${params}`);
  },

  // Entity-agnostic intake: drop a pile of files with no entity; each is
  // stored UNFILED and tagged with a suggested entity by the auto-matcher.
  async intake(files, { entityType, entityId } = {}) {
    const url = `${API_BASE}/api/documents/intake`;
    const fd = new FormData();
    Array.from(files || []).forEach((f) => fd.append('files', f));
    if (entityType) fd.append('entity_type', entityType);
    if (entityId) fd.append('entity_id', entityId);
    const response = await fetch(url, { method: 'POST', body: fd, credentials: 'include' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Intake failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return response.json();
  },

  // File an unfiled doc to an entity (+ optional slot); versions any doc
  // it supersedes in that (entity, slot).
  async assign(documentId, body) {
    return apiRequest(`/documents/${documentId}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },

  // Re-run the entity auto-matcher for an unfiled document.
  async matches(documentId) {
    return apiRequest(`/documents/${documentId}/matches`);
  },

  // Readiness rollup: suppliers missing required docs / expiring soon (+ the
  // capped list of suppliers needing attention).
  async readiness() {
    return apiRequest('/documents/readiness');
  },

  // Ask the supplier to renew an expiring/expired document (emails the
  // routed contact + records the request).
  async requestRenewal(documentId) {
    return apiRequest(`/documents/${documentId}/renewal-request`, { method: 'POST' });
  },

  // AI-extract expiry date + doc type from a stored document and fill any
  // blanks. Best-effort (no-op when AI is unavailable).
  async autoExtract(documentId) {
    return apiRequest(`/documents/${documentId}/auto-extract`, { method: 'POST' });
  },

  // Compliance-grade parse + normalize → typed `parsed_data` (proposed).
  async parse(documentId) {
    return apiRequest(`/documents/${documentId}/parse`, { method: 'POST' });
  },
  // Human-verification gate: approve proposed parsed fields (with optional
  // {key: value} corrections) → authoritative.
  async approveParsed(documentId, fields = {}) {
    return apiRequest(`/documents/${documentId}/parsed/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
  },

  // Free-form Q&A over a stored document (file attached to the prompt).
  async ask(documentId, question) {
    return apiRequest(`/documents/${documentId}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    });
  },

  // Per-user Documents-page preferences (starred / recents / saved views),
  // persisted server-side so they follow the user across devices.
  async getPrefs() {
    return apiRequest('/documents/prefs');
  },
  async savePrefs(prefs) {
    return apiRequest('/documents/prefs', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs),
    });
  },

  downloadUrl(documentId) {
    return `${API_BASE}/api/documents/${documentId}/download`;
  },

  // ── Drive-style user folders ──────────────────────────────────────
  // A nestable folder tree the user creates for anything that doesn't map
  // to an entity. Each node: {id, name, count, children:[…]}.
  async foldersTree() {
    return apiRequest('/documents/folders/tree');
  },
  async createFolder(name, parentId = null) {
    return apiRequest('/documents/folders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, parent_id: parentId }),
    });
  },
  // Partial update: pass only what changes. parent_id omitted = keep.
  async updateFolder(folderId, { name, parentId } = {}) {
    const body = {};
    if (name !== undefined) body.name = name;
    if (parentId !== undefined) body.parent_id = parentId;
    return apiRequest(`/documents/folders/${folderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  async deleteFolder(folderId) {
    return apiRequest(`/documents/folders/${folderId}`, { method: 'DELETE' });
  },
  // Place a document inside a user folder (folderId = null to un-file).
  async moveDocument(documentId, folderId) {
    return apiRequest(`/documents/${documentId}/move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder_id: folderId }),
    });
  },
  // Set the free-text label a document carries (its name in the drive).
  async labelDocument(documentId, label) {
    return apiRequest(`/documents/${documentId}/label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    });
  },

  // ── Library tree + production genealogy (auto-organized) ───────────
  // Members of a Library category (supplier/manufacturer/ingredient/
  // packaging), each with a document count — lazy-loaded on expand.
  async libraryMembers(entityType) {
    return apiRequest(`/documents/library/${entityType}`);
  },
  // Active compliance programs (THC/alcohol/beauty/food) + lab document
  // types, each with a count — for the sidebar Compliance + Lab sections.
  async complianceOverview() {
    return apiRequest('/documents/compliance/overview');
  },
  // Suppliers that have purchase-order documents, with counts.
  async poSuppliers() {
    return apiRequest('/documents/po-suppliers');
  },
  // Production runs as nodes, each with a document count.
  async productionRuns() {
    return apiRequest('/documents/production/runs');
  },
  // Everything traceable to a run: POs → lots → COAs + batch record + docs.
  async productionGenealogy(runId) {
    return apiRequest(`/documents/production/${runId}/genealogy`);
  },
  // Tag a document by lab type and/or compliance requirement slot (no entity).
  async classifyDocument(documentId, { docType, slot } = {}) {
    return apiRequest(`/documents/${documentId}/classify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_type: docType, slot }),
    });
  },
};

// ============================================
// ANALYTICS
// ============================================
export const analyticsAPI = {
  async getOverview(period = '30d') {
    return apiRequest(`/analytics/overview?period=${period}`);
  },

  async getOrdersByStatus() {
    return apiRequest('/analytics/orders-by-status');
  },

  async getTopProducts(limit = 5) {
    return apiRequest(`/analytics/top-products?limit=${limit}`);
  },

  async getRevenueChart(period = '30d') {
    return apiRequest(`/analytics/revenue-chart?period=${period}`);
  },

  async getCategoryBreakdown() {
    return apiRequest('/analytics/category-breakdown');
  },

  async getCustomerMetrics() {
    return apiRequest('/analytics/customer-metrics');
  }
};

// ============================================
// SAVED SEARCHES
// ============================================
export const savedSearchesAPI = {
  async getAll() {
    return apiRequest('/saved-searches');
  },

  async getById(searchId) {
    return apiRequest(`/saved-searches/${searchId}`);
  },

  async create(searchData) {
    return apiRequest('/saved-searches', {
      method: 'POST',
      body: JSON.stringify(searchData),
    });
  },

  async update(searchId, searchData) {
    return apiRequest(`/saved-searches/${searchId}`, {
      method: 'PUT',
      body: JSON.stringify(searchData),
    });
  },

  async delete(searchId) {
    return apiRequest(`/saved-searches/${searchId}`, {
      method: 'DELETE',
    });
  }
};

// ============================================
// ONBOARDING PACKET
// ============================================
export const onboardingAPI = {
  async get() {
    return apiRequest('/onboarding-packet');
  },

  async save(data) {
    return apiRequest('/onboarding-packet', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// NOTIFICATIONS
// ============================================
// (notificationsAPI removed — the in-app notification feed was excised.)

// ============================================
// AI RECOMMENDATIONS
// ============================================
export const recommendationsAPI = {
  async get() {
    return apiRequest('/recommendations');
  },
};

// ============================================
// FAVORITES
// ============================================
export const favoritesAPI = {
  async getAll() {
    return apiRequest('/favorites');
  },

  async add(productId) {
    return apiRequest('/favorites', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  },

  async remove(productId) {
    return apiRequest(`/favorites/${productId}`, {
      method: 'DELETE',
    });
  },

  async check(productId) {
    return apiRequest(`/favorites/check/${productId}`);
  },
};

// ============================================
// SCORECARDS
// ============================================
// Supplier scorecards were part of the removed RFQ / supplier-performance
// surface (backend route deleted in the brand-only MRP excision). Inert stubs
// so callers get an empty result instead of a 404.
export const scorecardsAPI = {
  async getAll() { return []; },
  async get() { return null; },
};

// Export a default API object with all services
const api = {
  auth: authAPI,
  products: productsAPI,
  tenants: tenantsAPI,
  orders: ordersAPI,
  savedSuppliers: savedSuppliersAPI,
  requests: requestsAPI,
  documents: documentsAPI,
  analytics: analyticsAPI,
  savedSearches: savedSearchesAPI,
  onboarding: onboardingAPI,
  recommendations: recommendationsAPI,
  favorites: favoritesAPI,
  scorecards: scorecardsAPI,
};

export default api;
